import React from 'react';
import { Search, X, AlertTriangle, Utensils, Ban } from 'lucide-react';
import { StatusFilterChips } from '../../ui/StatusFilterChips';
import {
    ConvivesFilters, EMPTY_FILTERS, Guest, filterGuests, guestAllergies, guestAltRequested, guestRestrictions,
    guestResponseState, guestSkippedCategories, hasActiveFilters,
} from './guestFilters';

/** Puce d'un filtre qui s'ajoute aux autres (et non un choix exclusif) — mêmes proportions et mêmes états
 *  visuels que StatusFilterChips, volontairement, pour que les deux rangées de filtres se lisent comme un
 *  seul bloc. StatusFilterChips n'est pas modifié : il est partagé avec Commandes et Extras, et ne sait
 *  gérer qu'un choix unique. */
function ToggleChip({ active, label, count, icon, onToggle }: {
    active: boolean; label: string; count: number; icon: React.ReactNode; onToggle: () => void;
}) {
    return (
        <button type="button" onClick={onToggle} aria-pressed={active}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                active
                    ? 'bg-brand-600 border-brand-600 text-white'
                    : 'bg-surface border-border text-content-muted hover:text-content hover:border-brand-500/40'
            }`}>
            {icon}
            {label}
            <span className={`text-[10px] ${active ? 'text-white/80' : 'text-content-muted'}`}>({count})</span>
        </button>
    );
}

/** Barre de recherche + filtres de la liste des convives. Les trois dimensions (réponse / contraintes
 *  alimentaires / alerte menu) se cumulent entre elles et avec la recherche. Chaque compteur est calculé
 *  en ignorant sa propre dimension mais en tenant compte des autres filtres actifs : la puce annonce donc
 *  bien ce qu'on obtiendra en cliquant dessus. Demande d'Emeni (2026-08-24). */
export function ConvivesFilterBar({ guests, filters, onChange, resultCount }: {
    guests: Guest[];
    filters: ConvivesFilters;
    onChange: (f: ConvivesFilters) => void;
    resultCount: number;
}) {
    const counts = React.useMemo(() => {
        const forResponse = filterGuests(guests, filters, 'response');
        return {
            non_invite: forResponse.filter((g) => guestResponseState(g) === 'non_invite').length,
            en_attente: forResponse.filter((g) => guestResponseState(g) === 'en_attente').length,
            repondu: forResponse.filter((g) => guestResponseState(g) === 'repondu').length,
            allergies: filterGuests(guests, filters, 'allergies').filter((g) => guestAllergies(g).length > 0).length,
            restrictions: filterGuests(guests, filters, 'restrictions').filter((g) => guestRestrictions(g).length > 0).length,
            // Même définition que le filtre lui-même (matchesFilters) : renoncer à une catégorie ET en
            // demander une autre comptent tous deux comme une alerte menu.
            menuAlert: filterGuests(guests, filters, 'menuAlert').filter((g) => guestSkippedCategories(g).length > 0 || guestAltRequested(g).length > 0).length,
        };
    }, [guests, filters]);

    const active = hasActiveFilters(filters);

    return (
        <div className="space-y-2.5">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input value={filters.search} onChange={(e) => onChange({ ...filters, search: e.target.value })}
                        placeholder="Rechercher un convive (nom, email)..."
                        className="w-full pl-7 pr-3 py-1.5 rounded-lg bg-surface border border-border text-xs text-content outline-none focus:border-brand-500" />
                </div>
                <span className="text-[11px] font-bold text-content-muted whitespace-nowrap">
                    {resultCount} / {guests.length}
                </span>
                {active && (
                    <button type="button" onClick={() => onChange(EMPTY_FILTERS)}
                        className="flex items-center gap-1 text-[11px] font-bold text-content-muted hover:text-content">
                        <X size={12} /> Réinitialiser
                    </button>
                )}
            </div>

            <StatusFilterChips
                value={filters.response}
                onChange={(v) => onChange({ ...filters, response: v as ConvivesFilters['response'] })}
                options={[
                    { value: 'non_invite', label: 'Non invité', count: counts.non_invite },
                    { value: 'en_attente', label: 'En attente', count: counts.en_attente },
                    { value: 'repondu', label: 'Répondu', count: counts.repondu },
                ]}
            />

            <div className="flex items-center gap-2 flex-wrap">
                <ToggleChip active={filters.withAllergies} label="Allergies déclarées" count={counts.allergies}
                    icon={<AlertTriangle size={12} />}
                    onToggle={() => onChange({ ...filters, withAllergies: !filters.withAllergies })} />
                <ToggleChip active={filters.withRestrictions} label="Restrictions alimentaires" count={counts.restrictions}
                    icon={<Utensils size={12} />}
                    onToggle={() => onChange({ ...filters, withRestrictions: !filters.withRestrictions })} />
                {/* Libellé élargi depuis que le convive peut aussi demander une alternative : "n'a pas choisi
                    de plat" ne décrivait plus que la moitié de ce que la puce ramène. */}
                <ToggleChip active={filters.withMenuAlert} label="Sans plat ou en attente" count={counts.menuAlert}
                    icon={<Ban size={12} />}
                    onToggle={() => onChange({ ...filters, withMenuAlert: !filters.withMenuAlert })} />
            </div>
        </div>
    );
}
